/**
 * Account Settings Modal
 * 
 * Account management modal accessible from the landing page.
 * Includes: Account Tier, Change Password, Delete Account, Sign Out
 */

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlayer } from '../contexts/PlayerContext';
import { 
  updatePassword, 
  reauthenticateWithCredential, 
  EmailAuthProvider,
  deleteUser
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, deleteDoc } from 'firebase/firestore';
import './AccountSettingsModal.css';

interface AccountSettingsModalProps {
  onClose: () => void;
  onAccountDeleted?: () => void;
}

export function AccountSettingsModal({ onClose, onAccountDeleted }: AccountSettingsModalProps): React.ReactElement {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { player } = usePlayer();
  
  // UI state
  const [currentView, setCurrentView] = useState<'main' | 'changePassword' | 'deleteAccount' | 'accountDeleted'>('main');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Change Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Delete Account state
  const [deleteConfirmPassword, setDeleteConfirmPassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Get account tier (default to "free" for now)
  const accountTier = player?.tier || 'free';
  const maxStamina = accountTier === 'paid' ? 24 : 12;

  // ============================================================================
  // Change Password
  // ============================================================================

  const handleChangePassword = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return;
    }

    setIsProcessing(true);

    try {
      // Re-authenticate user before changing password
      if (!auth.currentUser || !user?.email) {
        throw new Error('No user logged in');
      }

      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Update password
      await updatePassword(auth.currentUser, newPassword);

      setSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Return to main view after 2 seconds
      setTimeout(() => {
        setCurrentView('main');
        setSuccess(null);
      }, 2000);
    } catch (err: any) {
      console.error('Change password error:', err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Current password is incorrect');
      } else if (err.code === 'auth/weak-password') {
        setError('New password is too weak');
      } else {
        setError(err.message || 'Failed to change password');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // ============================================================================
  // Delete Account
  // ============================================================================

  const handleDeleteAccount = async () => {
    setError(null);

    // Validation
    if (!deleteConfirmPassword) {
      setError('Please enter your password to confirm');
      return;
    }

    if (deleteConfirmation.toLowerCase() !== 'delete') {
      setError('Please type "DELETE" to confirm account deletion');
      return;
    }

    setIsProcessing(true);

    try {
      if (!auth.currentUser || !user?.email || !user?.uid) {
        throw new Error('No user logged in');
      }

      // Re-authenticate before deletion
      const credential = EmailAuthProvider.credential(user.email, deleteConfirmPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Delete player data from Firestore
      const playerDocRef = doc(db, 'players', user.uid);
      await deleteDoc(playerDocRef);

      console.log('✅ Player data deleted from Firestore');

      // Delete Firebase Auth account
      await deleteUser(auth.currentUser);

      console.log('✅ Firebase Auth account deleted');

      // Show success message
      setCurrentView('accountDeleted');
      
      // Notify parent component if callback provided
      if (onAccountDeleted) {
        onAccountDeleted();
      }

      // Auto-close and sign out after 3 seconds
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (err: any) {
      console.error('Delete account error:', err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Password is incorrect');
      } else {
        setError(err.message || 'Failed to delete account');
      }
      setIsProcessing(false);
    }
  };

  // ============================================================================
  // Sign Out
  // ============================================================================

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Sign out error:', err);
      setError('Failed to sign out');
    }
  };

  // ============================================================================
  // Render Views
  // ============================================================================

  // Account Deleted Success View
  if (currentView === 'accountDeleted') {
    return (
      <div className="account-settings-overlay" onClick={onClose}>
        <div className="account-settings-modal" onClick={(e) => e.stopPropagation()}>
          <div className="account-settings-success">
            <div className="success-icon">✓</div>
            <h2>Account Successfully Deleted</h2>
            <p>Your account and all associated data have been permanently removed.</p>
            <p style={{ fontSize: '14px', color: '#888', marginTop: '16px' }}>
              You will be returned to the login screen shortly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Change Password View
  if (currentView === 'changePassword') {
    return (
      <div className="account-settings-overlay" onClick={onClose}>
        <div className="account-settings-modal" onClick={(e) => e.stopPropagation()}>
          <div className="account-settings-header">
            <button className="back-button" onClick={() => setCurrentView('main')}>← Back</button>
            <h2>Change Password</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>

          <div className="account-settings-content">
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                disabled={isProcessing}
              />
            </div>

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                disabled={isProcessing}
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={isProcessing}
              />
            </div>

            <button
              className="primary-button"
              onClick={handleChangePassword}
              disabled={isProcessing}
            >
              {isProcessing ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Delete Account View
  if (currentView === 'deleteAccount') {
    return (
      <div className="account-settings-overlay" onClick={onClose}>
        <div className="account-settings-modal" onClick={(e) => e.stopPropagation()}>
          <div className="account-settings-header">
            <button className="back-button" onClick={() => setCurrentView('main')}>← Back</button>
            <h2>Delete Account</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>

          <div className="account-settings-content">
            <div className="danger-warning">
              <strong>⚠️ Warning: This action cannot be undone!</strong>
              <p>Deleting your account will permanently remove:</p>
              <ul>
                <li>Your character and all progress</li>
                <li>All explored hexes and discoveries</li>
                <li>Your Lore Codex and quest completions</li>
                <li>Your account credentials</li>
              </ul>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={deleteConfirmPassword}
                onChange={(e) => setDeleteConfirmPassword(e.target.value)}
                placeholder="Enter your password to confirm"
                disabled={isProcessing}
              />
            </div>

            <div className="form-group">
              <label>Type "DELETE" to confirm</label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type DELETE in all caps"
                disabled={isProcessing}
              />
            </div>

            <button
              className="danger-button"
              onClick={handleDeleteAccount}
              disabled={isProcessing || deleteConfirmation.toLowerCase() !== 'delete'}
            >
              {isProcessing ? 'Deleting Account...' : 'Permanently Delete Account'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main View
  return ReactDOM.createPortal(
    <div className="account-settings-overlay" onClick={onClose}>
      <div className="account-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="account-settings-header">
          <h2>Account Settings</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="account-settings-content">
          {/* Account Info */}
          <div className="settings-section">
            <h3>Account Information</h3>
            <div className="info-row">
              <span className="info-label">Email:</span>
              <span className="info-value">{user?.email}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Account Tier:</span>
              <span className="info-value tier-badge">
                {accountTier === 'paid' ? 'Paid' : 'Free'} ({maxStamina} stamina/day)
              </span>
            </div>
          </div>

          {/* Account Actions */}
          <div className="settings-section">
            <h3>Account Actions</h3>
            
            <button
              className="settings-button"
              onClick={() => setCurrentView('changePassword')}
            >
              🔑 Change Password
            </button>

            <button
              className="settings-button sign-out-button"
              onClick={handleSignOut}
            >
              🚪 Sign Out
            </button>
          </div>

          {/* Danger Zone */}
          <div className="settings-section danger-zone">
            <h3>Danger Zone</h3>
            <button
              className="danger-button"
              onClick={() => setCurrentView('deleteAccount')}
            >
              🗑️ Delete Account
            </button>
            <p className="danger-warning-text">
              Permanently delete your account and all associated data.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

